import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import RoomIcon from '@mui/icons-material/Room';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Box, styled } from '@mui/system';
import axios from 'axios';
import * as L from 'leaflet/dist/leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.icon.glyph';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loading from '../components/layouts/Loading';
import { apiUrl } from '../constants';
import { AuthContext } from '../contexts/AuthContext';
import { showToast } from '../redux/actions';
import { updateAvatar, updatePersonal } from '../redux/actions/customers';
import { toast$ } from '../redux/selectors';

const AlertMessage = lazy(() => import('../components/layouts/AlertMessage'));
const Tooltip = lazy(() => import('../components/layouts/Tooltip'));

const CoinButton = styled(Button)`
  font-weight: 900;
  font-size: 1.5rem;

  span {
    svg {
      font-size: 35px !important;
    }
  }
`;

const PersonalInfo = () => {
  const {
    authState: { user, isAuthenticated, authLoading },
    loadUser,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useSelector(toast$);
  const [alert, setAlert] = useState(null);
  const [newInfo, setNewInfo] = useState({
    fullName: '',
    phone: '',
    address: '',
    avatar: '',
    province: '',
    district: '',
    ward: '',
    street: '',
  });
  const { fullName, phone, address, avatar, province, district, ward, street } =
    newInfo;
  const [provinceData, setProvinceData] = useState({ data: '' });
  const [districtData, setDistrictData] = useState({ data: '' });
  const [wardData, setWardData] = useState({ data: '' });
  const [isDisableSubmit, setIsDisableSubmit] = useState(false);
  const [myLatlng, setMyLatlng] = useState({ lat: '', lng: '' });
  const { t } = useTranslation();

  if (!authLoading && !isAuthenticated) {
    navigate(`/login?RedirectTo=${location.pathname}${location.search}`);
  }

  useEffect(() => {
    const getProvince = async () => {
      axios.defaults.headers.common[
        'Token'
      ] = `${process.env.REACT_APP_GHN_TOKEN_API}`;
      const provinceDataApi = await axios.get(
        'https://dev-online-gateway.ghn.vn/shiip/public-api/master-data/province'
      );
      setProvinceData({ data: provinceDataApi.data.data });
    };
    getProvince();
    return () => delete axios.defaults.headers.common['Token'];
  }, []);

  useEffect(() => {
    const getDistrict = async () => {
      if (newInfo.province.ProvinceID) {
        const districtDataApi = await axios.post(
          'https://dev-online-gateway.ghn.vn/shiip/public-api/master-data/district',
          {
            province_id: newInfo.province.ProvinceID,
          }
        );
        setDistrictData({ data: districtDataApi.data.data });
      }
    };
    getDistrict();
  }, [newInfo.province.ProvinceID]);

  useEffect(() => {
    const getWard = async () => {
      if (newInfo.district.DistrictID) {
        const districtDataApi = await axios.post(
          'https://dev-online-gateway.ghn.vn/shiip/public-api/master-data/ward?district_id',
          {
            district_id: newInfo.district.DistrictID,
          }
        );
        setWardData({ data: districtDataApi.data.data });
      }
    };
    getWard();
  }, [newInfo.district.DistrictID]);

  const clearData = useCallback(() => {
    setNewInfo({
      fullName: user?.fullName || '',
      phone: user?.phone || '',
      address: user?.address || '',
      avatar: user?.avatar || '',
      province: '',
      district: '',
      ward: '',
      street: '',
    });
    setDistrictData({ data: '' });
    setWardData({ data: '' });
  }, [user?.address, user?.avatar, user?.fullName, user?.phone]);

  useEffect(
    () => async () => {
      await loadUser();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authLoading]
  );

  useEffect(() => {
    clearData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUser]);

  const onFileChange = useCallback(
    (event) => {
      const formData = new FormData();
      formData.append('file', event.target.files[0]);
      dispatch(
        updateAvatar.updateAvatarRequest({
          id: user?.id,
          formData,
        })
      );
      dispatch(
        showToast({
          message: t('update2'),
          type: 'warning',
        })
      );
      setTimeout(() => {
        if (toast?.type === 'success') window.location.reload();
      }, 5000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, toast?.type, user?.id]
  );

  const onChangeData = useCallback(
    (event) => {
      setNewInfo({ ...newInfo, [event.target.name]: event.target.value });
    },
    [newInfo]
  );

  const onSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setIsDisableSubmit(true);
      if (ward) {
        if (!street) {
          setIsDisableSubmit(false);
          setAlert({
            type: 'warning',
            message: t('addressInfo'),
          });
          setTimeout(() => setAlert(null), 5000);
          return;
        } else {
          setNewInfo({
            ...newInfo,
            wardCode: ward.WardCode,
            districtId: district.DistrictID,
            address: `${street}, ${ward.WardName}, ${district.DistrictName}, ${province.ProvinceName}`,
          });
        }
      }
      if (
        fullName === user?.fullName &&
        phone === user?.phone &&
        address === user?.address
      ) {
        console.log('nothing change');
        setIsDisableSubmit(false);
        return;
      }
      if (!fullName || !phone) {
        setIsDisableSubmit(false);
        setAlert({
          type: 'warning',
          message: t('completeInfo'),
        });
        setTimeout(() => setAlert(null), 5000);
        return;
      }
      setAlert(null);
      dispatch(
        updatePersonal.updatePersonalRequest({ id: user?.id, ...newInfo })
      );
      dispatch(
        showToast({
          message: t('update2'),
          type: 'warning',
        })
      );
      setTimeout(() => {
        if (toast?.type === 'success') window.location.reload();
      }, 3000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      address,
      dispatch,
      district.DistrictID,
      district.DistrictName,
      fullName,
      newInfo,
      phone,
      province.ProvinceName,
      street,
      toast?.type,
      user?.address,
      user?.fullName,
      user?.id,
      user?.phone,
      ward,
    ]
  );

  useEffect(() => {
    const myMap = async () => {
      if (user) {
        let userLatlng = [user?.latlng?.lat, user?.latlng?.lng];
        if (!user?.latlng) {
          const res = await axios.post(`${apiUrl}/map/latlng`, {
            address: user?.address,
          });
          userLatlng = [res?.data?.latitude, res?.data?.longitude];
        }

        // set 2 marker
        const startCoordinate = [10.847736, 106.777486];
        setMyLatlng({ ...myLatlng, lat: userLatlng[0], lng: userLatlng[1] });

        // set map
        let map;
        L.map('map').remove();
        map = L.map('map').setView(
          userLatlng[0] ? userLatlng : startCoordinate,
          16
        );
        L.tileLayer(
          'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
          {
            attribution:
              'Map data &copy; <a href="https://fb.com/huynhjs">Phu Nhut Huynh</a>',
            id: 'mapbox/streets-v12',
            tileSize: 512,
            zoomOffset: -1,
            accessToken: process.env.REACT_APP_MAP_KEY,
          }
        ).addTo(map);

        // start
        var markerStore = L.marker(startCoordinate).addTo(map);
        markerStore.setIcon(
          L.icon.glyph({
            prefix: 'mdi',
            glyph: 'store',
          })
        );
        markerStore.bindPopup('<b>Hello!</b><br>We are YumYum.').openPopup();

        var markerAddress;
        if (userLatlng[0]) {
          markerAddress = L.marker(userLatlng).addTo(map);
          markerAddress.setIcon(
            L.icon.glyph({
              prefix: 'mdi',
              glyph: 'home',
            })
          );
          var group = new L.featureGroup([markerAddress]);
          map.fitBounds(group.getBounds());
        }
        map.on('click', function (e) {
          if (markerAddress) {
            map?.removeLayer(markerAddress);
          }
          markerAddress = L.marker([e.latlng.lat, e.latlng.lng]).addTo(map);
          markerAddress.setIcon(
            L.icon.glyph({
              prefix: 'mdi',
              glyph: 'home',
            })
          );
          setMyLatlng({ ...myLatlng, lat: e.latlng.lat, lng: e.latlng.lng });
        });
      }
    };
    myMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateCoordinate = () => {
    dispatch(
      updatePersonal.updatePersonalRequest({
        id: user?.id,
        ...newInfo,
        latlng: { lat: myLatlng.lat, lng: myLatlng.lng },
        isIgnoreCheck: true,
      })
    );
    dispatch(
      showToast({
        message: t('update2'),
        type: 'warning',
      })
    );
    setTimeout(() => {
      if (toast?.type === 'success') window.location.reload();
    }, 3000);
  };

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Tooltip toast={toast} />
      </Suspense>
      <section
        className="breadcrumb-section set-bg"
        style={{
          backgroundImage: `url(img/breadcrumb.jpg)`,
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <div className="breadcrumb__text">
                <h2>{t('personalInfo.title')}</h2>
                <div className="breadcrumb__option">
                  <Link to="/">{t('header.home')}</Link>
                  <span>{t('personalInfo.title2')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="checkout">
        <div className="container" style={{ marginBottom: '30px' }}>
          <div className="checkout__form">
            {alert && (
              <Suspense fallback={<Loading />}>
                <AlertMessage info={alert} />
              </Suspense>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <div className="row">
                <div className="col-lg-8 col-md-8">
                  <div className="checkout__input">
                    <p>
                      {t('personalInfo.name')}
                      <span>*</span>
                    </p>
                    <input
                      type="text"
                      value={fullName}
                      name="fullName"
                      onChange={onChangeData}
                    />
                  </div>
                  <div className="checkout__input">
                    <p>
                      {t('personalInfo.address')}
                      <span>*</span>
                    </p>
                    <input type="text" defaultValue={address} disabled />
                    {provinceData.data?.length > 0 && (
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="standard"
                        select
                        label={t('personalInfo.province')}
                        name="province"
                        value={province ?? 201}
                        onChange={onChangeData}
                      >
                        {provinceData.data.map((subProvinceData) => (
                          <MenuItem
                            key={subProvinceData.ProvinceID}
                            value={subProvinceData}
                          >
                            {subProvinceData.ProvinceName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {districtData.data?.length > 0 && (
                      <TextField
                        margin="dense"
                        required
                        fullWidth
                        variant="standard"
                        select
                        label={t('personalInfo.district')}
                        name="district"
                        value={district}
                        onChange={onChangeData}
                      >
                        {districtData.data.map((subDistrictData) => (
                          <MenuItem
                            key={subDistrictData.DistrictID}
                            value={subDistrictData}
                          >
                            {subDistrictData.DistrictName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {wardData.data?.length > 0 && (
                      <TextField
                        margin="dense"
                        required
                        fullWidth
                        variant="standard"
                        select
                        label={t('personalInfo.ward')}
                        name="ward"
                        value={ward}
                        onChange={onChangeData}
                      >
                        {wardData.data.map((subWardData) => (
                          <MenuItem
                            key={subWardData.WardCode}
                            value={subWardData}
                          >
                            {subWardData.WardName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {newInfo?.ward?.WardCode && (
                      <TextField
                        required
                        fullWidth
                        label={t('personalInfo.detailAddress')}
                        autoComplete="given-name"
                        autoFocus
                        onChange={onChangeData}
                        name="street"
                        value={street}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </div>
                  <div className="checkout__input">
                    <p>
                      {t('personalInfo.phone')}
                      <span>*</span>
                    </p>
                    <input
                      type="text"
                      value={phone}
                      name="phone"
                      onChange={onChangeData}
                    />
                  </div>
                  <div className="checkout__input">
                    <p>
                      Email<span>*</span>
                    </p>
                    <input type="text" defaultValue={user?.email} disabled />
                  </div>
                </div>
                <div className="col-lg-4 col-md-4">
                  <div className="checkout__order">
                    <Box>
                      {/* <img
                        alt="avatar"
                        src={
                          avatar?.imageUrl ||
                          'https://i1.wp.com/www.polar-pinguin.berlin/wp-content/uploads/2017/12/image_preview.png?w=1080&ssl=1'
                        }
                      /> */}
                      <LazyLoadImage
                        effect="blur"
                        src={
                          avatar?.imageUrl ||
                          'https://i1.wp.com/www.polar-pinguin.berlin/wp-content/uploads/2017/12/image_preview.png?w=1080&ssl=1'
                        }
                        alt="avatar"
                        placeholderSrc="img/logo.png"
                      />
                    </Box>
                    <label
                      htmlFor="contained-button-file"
                      style={{ width: '100%', marginTop: '5px' }}
                    >
                      <Input
                        accept="image/*"
                        id="contained-button-file"
                        multiple={false}
                        type="file"
                        sx={{ display: 'none' }}
                        onChange={onFileChange}
                      />
                      <Button
                        variant="contained"
                        component="span"
                        size="small"
                        fullWidth
                        startIcon={<CameraAltIcon />}
                      >
                        {t('personalInfo.btn.upload')}
                      </Button>
                    </label>
                    <button
                      onClick={clearData}
                      className="site-btn"
                      style={{ background: '#94998d' }}
                    >
                      {t('personalInfo.btn.cancel')}
                    </button>
                    <button
                      onClick={onSubmit}
                      className="site-btn"
                      disabled={isDisableSubmit}
                      style={{
                        background: isDisableSubmit
                          ? 'rgb(148, 153, 141)'
                          : '#7fad39',
                      }}
                    >
                      {t('personalInfo.btn.update')}
                    </button>
                  </div>
                  <CoinButton
                    variant="contained"
                    color="warning"
                    fullWidth
                    margin="dense"
                    size="large"
                    startIcon={<CurrencyBitcoinIcon />}
                    sx={{ mt: 1 }}
                  >
                    {String(user?.coin || 0).replace(
                      /(.)(?=(\d{3})+$)/g,
                      '$1,'
                    )}
                  </CoinButton>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div
          className="checkout__input"
          style={{
            zIndex: 1,
            maxWidth: '400px',
            margin: 'auto',
            display: 'flex',
            gap: '5px',
            marginBottom: '10px',
          }}
        >
          {/* <p>{t('personalInfo.coordinate')}</p> */}
          <input
            type="text"
            value={`${myLatlng.lat}, ${myLatlng.lng}`}
            onChange={() => {}}
            disabled
          />
          <Button
            variant="contained"
            component="span"
            size="small"
            fullWidth
            startIcon={<RoomIcon />}
            onClick={updateCoordinate}
          ></Button>
        </div>
        <div
          id="map"
          style={{
            height: '700px',
            zIndex: 1,
            maxWidth: '1200px',
            margin: 'auto',
          }}
        ></div>
      </section>
    </>
  );
};

export default memo(PersonalInfo);
